import type {TFunction} from "i18next";

import {
    COMMISSION_ORDER_TYPES,
    type CommissionOrderType,
    type ValueOrderEntitlementOrderType,
    type ValueOrderEntitlementsModel,
} from "@reva/shared/value-order";

import {propertyTypeLabelKey, type ValueOrderPropertyType} from "./valueOrderPropertyType";

/**
 * Wie REVA das Beauftragen anbietet:
 * - `unrestricted`: wie ohne Rechteprüfung – Button mit allen Auftragsarten (auch wenn nichts ableitbar ist).
 * - `restricted`: Button, der Dialog bietet nur die freigeschalteten Auftragsarten an.
 * - `blocked`: statt des Buttons ein Hinweis, was beim Auftraggeber fehlt.
 * - `unavailable`: Button ausgegraut mit Hinweis, dass die Orders-API der ValueAG nicht erreichbar ist.
 */
export type CommissionEntitlementsMode = "unrestricted" | "restricted" | "blocked" | "unavailable";

export interface CommissionEntitlementsEvaluation {
    mode: CommissionEntitlementsMode;
    /** Die Orders-API lehnt den Auftraggeber für REVA insgesamt ab. */
    clientNotPermitted: boolean;
    /** Der eigenen Lizenz fehlt die Value-Client-ID – reva-rest lehnt das Beauftragen dann sicher ab. */
    noClientId: boolean;
    /** Anzubietende Auftragsarten; `null` = keine Einschränkung. */
    allowedOrderTypes: CommissionOrderType[] | null;
    /** Nicht freigeschaltete Auftragsarten (nur bei gelesener Konfiguration befüllt). */
    missingOrderTypes: CommissionOrderType[];
    /** Gebäudeart der Bewertung, falls sie beim Auftraggeber nicht freigeschaltet ist. */
    missingPropertyType: ValueOrderPropertyType | null;
    /**
     * Anzubietende Dialog-Optionen je Auftragsart (siehe {@link COMMISSION_ORDER_OPTION_KEYS}); fehlt eine Auftragsart,
     * werden ihre Optionen nicht eingeschränkt.
     */
    allowedOrderOptions: AllowedCommissionOrderOptions;
}

/** Auftragsarten, deren Dialog-Optionen einzeln beim Auftraggeber freigeschaltet sein müssen. */
export type CommissionOrderTypeWithOptions = "InteriorInspection" | "DocumentCollection";

/** Freigeschaltete Dialog-Optionen je Auftragsart; ein fehlender Eintrag bedeutet „nicht einschränken“. */
export type AllowedCommissionOrderOptions = Partial<Record<CommissionOrderTypeWithOptions, string[]>>;

/**
 * Zuordnung der Optionen im Beauftragungsdialog zu den Flag-Werten (`key`) der `OrderOptions` der Orders-API.
 * Dokumentenprüfung (32768) u. Ä. bietet der Dialog nicht an, sie fehlen hier bewusst.
 */
export const COMMISSION_ORDER_OPTION_KEYS: Readonly<
    Record<CommissionOrderTypeWithOptions, Readonly<Record<string, number>>>
> = {
    InteriorInspection: {
        GrossFloorArea: 4,
        FloorPlan: 8,
        LivingSpaceCalculation: 16,
    },
    DocumentCollection: {
        declarationOfCondominium: 32,
        partitionPlan: 64,
        registrationApproval: 128,
        landRegister: 256,
        officialCadastralMap: 1024,
        informationOnBuildingEncumbrances: 2048,
        informationOnContaminatedSites: 4096,
        nonOfficialCadastralMap: 8192,
    },
};

/**
 * Dialog-Optionen einer Auftragsart, die beim Auftraggeber freigeschaltet sind, in der Reihenfolge von
 * {@link COMMISSION_ORDER_OPTION_KEYS}. `null`, wenn reva-rest `orderOptionKeys` nicht liefert (älterer Stand) –
 * dann wird nicht gefiltert. Eine leere Liste heißt dagegen: keine der Dialog-Optionen ist freigeschaltet.
 * @param orderType Auftragsart mit Dialog-Optionen
 * @param entry Eintrag der Auftragsart aus `orderTypes`
 */
export function allowedCommissionOrderOptions(
    orderType: CommissionOrderTypeWithOptions,
    entry: ValueOrderEntitlementOrderType | null | undefined,
): string[] | null {
    if (!entry || !Array.isArray(entry.orderOptionKeys)) return null;
    const keys = new Set(entry.orderOptionKeys);
    return Object.entries(COMMISSION_ORDER_OPTION_KEYS[orderType])
        .filter(([, key]) => keys.has(key))
        .map(([option]) => option);
}

/**
 * Ob der Auftraggeber der eigenen Lizenz die Auftragsart REVA nachweislich nicht freigeschaltet hat – dann lassen
 * sich keine REVA-IDs anlegen. Nur bei gelesener Konfiguration (`OK`) mit einem `Reva`-Eintrag; fehlt der Eintrag
 * (älteres reva-rest), ist die Abfrage gescheitert oder der Status ein anderer, gilt das nicht als Sperre.
 * @param entitlements Antwort von `GET /value-orders/entitlements` (null/undefined = nicht geladen oder Fehler)
 */
export function isRevaOrderTypeNotEnabled(entitlements: ValueOrderEntitlementsModel | null | undefined): boolean {
    if (entitlements?.status !== "OK" || !Array.isArray(entitlements.orderTypes)) return false;
    const reva = entitlements.orderTypes.find((o) => o?.orderType === "Reva");
    return reva?.allowed === false;
}

const UNRESTRICTED: CommissionEntitlementsEvaluation = {
    mode: "unrestricted",
    clientNotPermitted: false,
    noClientId: false,
    allowedOrderTypes: null,
    missingOrderTypes: [],
    missingPropertyType: null,
    allowedOrderOptions: {},
};

/**
 * Entscheidet anhand der Freischaltungen beim ValueAG-Auftraggeber, ob und wie REVA das Beauftragen anbietet.
 * `OK`, `CLIENT_NOT_PERMITTED` und `NO_CLIENT_ID` schränken ein (bei den letzten beiden scheitert das Absenden
 * sicher); bei `UNAVAILABLE` ist die Orders-API nicht erreichbar, das Absenden scheiterte also ebenfalls – der Button
 * wird ausgegraut. Fehlende oder unvollständige Daten, `UNRECOGNIZED` und unbekannte Status lassen alles wie ohne
 * Prüfung: Das sind Lücken auf unserer Seite, die das Beauftragen nicht verbauen dürfen.
 * @param entitlements Antwort von `GET /value-orders/entitlements` (null/undefined = nicht geladen oder Fehler)
 * @param propertyType Gebäudeart der Bewertung; `NotDefined` (noch kein Segment) überspringt die Gebäudeart-Prüfung
 */
export function evaluateCommissionEntitlements(
    entitlements: ValueOrderEntitlementsModel | null | undefined,
    propertyType: ValueOrderPropertyType,
): CommissionEntitlementsEvaluation {
    if (!entitlements) return UNRESTRICTED;
    if (entitlements.status === "CLIENT_NOT_PERMITTED") {
        return {...UNRESTRICTED, mode: "blocked", clientNotPermitted: true, allowedOrderTypes: []};
    }
    if (entitlements.status === "UNAVAILABLE") {
        return {...UNRESTRICTED, mode: "unavailable", allowedOrderTypes: []};
    }
    if (entitlements.status === "NO_CLIENT_ID") {
        return {...UNRESTRICTED, mode: "blocked", noClientId: true, allowedOrderTypes: []};
    }
    if (entitlements.status !== "OK" || !Array.isArray(entitlements.orderTypes)) return UNRESTRICTED;

    const allowedByBackend = new Set(entitlements.orderTypes.filter((o) => o?.allowed).map((o) => o.orderType));
    const allowedOrderOptions: AllowedCommissionOrderOptions = {};
    for (const orderType of Object.keys(COMMISSION_ORDER_OPTION_KEYS) as CommissionOrderTypeWithOptions[]) {
        const options = allowedCommissionOrderOptions(
            orderType,
            entitlements.orderTypes.find((o) => o?.orderType === orderType),
        );
        if (options) allowedOrderOptions[orderType] = options;
    }
    // Ohne eine der Dialog-Optionen (z. B. nur Dokumentenprüfung) ließe sich keine Dokumentenbeschaffung absenden.
    if (allowedOrderOptions.DocumentCollection?.length === 0) {
        allowedByBackend.delete("DocumentCollection");
    }
    const allowedOrderTypes = COMMISSION_ORDER_TYPES.filter((o) => allowedByBackend.has(o));
    const missingOrderTypes = COMMISSION_ORDER_TYPES.filter((o) => !allowedByBackend.has(o));
    const missingPropertyType =
        propertyType !== "NotDefined" &&
        Array.isArray(entitlements.propertyTypes) &&
        !entitlements.propertyTypes.includes(propertyType)
            ? propertyType
            : null;

    let mode: CommissionEntitlementsMode = "unrestricted";
    if (allowedOrderTypes.length === 0 || missingPropertyType) {
        mode = "blocked";
    } else if (missingOrderTypes.length > 0) {
        mode = "restricted";
    }
    return {
        mode,
        clientNotPermitted: false,
        noClientId: false,
        allowedOrderTypes: mode === "unrestricted" ? null : allowedOrderTypes,
        missingOrderTypes,
        missingPropertyType,
        allowedOrderOptions,
    };
}

const ORDER_TYPE_LABEL_KEYS: Record<CommissionOrderType, string> = {
    ExteriorInspection: "commissionOrderDialog.exterior",
    InteriorInspection: "commissionOrderDialog.interior",
    DocumentCollection: "commissionOrderDialog.documentCollection",
};

/**
 * i18n-Schlüssel des Anzeigenamens einer Auftragsart (dieselben Namen wie im Beauftragungsdialog).
 * @param orderType REVA-Auftragsart
 */
export function orderTypeLabelKey(orderType: CommissionOrderType): string {
    return ORDER_TYPE_LABEL_KEYS[orderType];
}

/**
 * Lesbare Liste der Auftragsarten, z. B. „Außenbesichtigung, Dokumentenbeschaffung".
 * @param t Übersetzungsfunktion
 * @param orderTypes Auftragsarten
 */
export function formatOrderTypeList(t: TFunction, orderTypes: CommissionOrderType[]): string {
    return orderTypes.map((o) => t(orderTypeLabelKey(o))).join(", ");
}

/**
 * Lesbarer Name einer Gebäudeart; unbekannte Namen erscheinen unverändert.
 * @param t Übersetzungsfunktion
 * @param propertyType Gebäudeart-Name wie von `segmentBuildingTypeToPropertyType`
 */
export function propertyTypeLabel(t: TFunction, propertyType: string): string {
    return t(propertyTypeLabelKey(propertyType), {defaultValue: propertyType});
}

/**
 * Hinweiszeilen für den Fall `blocked`: was beim Auftraggeber fehlt, eine Zeile je Lücke. Leer, wenn nichts blockiert.
 * @param t Übersetzungsfunktion
 * @param evaluation Ergebnis von {@link evaluateCommissionEntitlements}
 */
export function describeMissingEntitlements(t: TFunction, evaluation: CommissionEntitlementsEvaluation): string[] {
    if (evaluation.mode !== "blocked" || evaluation.clientNotPermitted || evaluation.noClientId) return [];
    const lines: string[] = [];
    if (evaluation.missingOrderTypes.length > 0) {
        lines.push(
            t("order.revaOrder.entitlements.missingOrderTypes", {
                count: evaluation.missingOrderTypes.length,
                list: formatOrderTypeList(t, evaluation.missingOrderTypes),
            }),
        );
    }
    if (evaluation.missingPropertyType) {
        lines.push(
            t("order.revaOrder.entitlements.missingPropertyType", {
                name: propertyTypeLabel(t, evaluation.missingPropertyType),
            }),
        );
    }
    return lines;
}
